/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Alert, Cell, FormLabel, Grid, Heading, HFlow, Text, Tooltip, VFlow } from 'bold-ui'
import { Box } from 'components/Box'
import { CheckboxField, FormPrompt, TextAreaField } from 'components/form'
import { TipoReceitaSelectField } from 'components/form/field/select/TipoReceitaSelectField'
import { ViaAdministracaoSelectField } from 'components/form/field/select/ViaAdministracaoSelectField'
import { PageLoading } from 'components/loading'
import theme from 'config/theme'
import { useFlags } from 'config/useFlagsContext'
import { useMedicoesAtendimentosAnterioresQuery } from 'graphql/hooks.generated'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import { useMemoWithDeepCompare } from 'hooks/useMemoWithDeepCompare'
import React, { useEffect, useMemo, useState } from 'react'
import { FormRenderProps, useField } from 'react-final-form'
import { tipoReceitaRecord } from 'types/enums'
import { metaPath } from 'util/metaPath'
import { isEmpty } from 'util/validation/Util'
import { MedicaoAnteriorFormModel } from 'view/atendimentos/detail/components/modals/medicoes/MedicaoAnteriorForm'
import {
  calculatePeso,
  mountMedicoesNoNascimento,
} from 'view/atendimentos/detail/components/modals/medicoes/util-medicoes'

import { handleKeyEnter } from '../../../util'
import AlertPrescricaoModal, { AlertPrescricaoEnum } from '../components/AlertPrescricaoModal'
import FrequenciaTabs from '../components/FrequenciaTabs'
import { usePrescricaoFormManager } from '../hooks/usePrescricaoFormManager'
import { usePrescricaoHorusIntegration } from '../hooks/usePrescricaoHorusIntegration'
import { MedicamentoFormModel } from '../model-prescricao'
import { calculateDataFimTratamento, createPosologiaLabel } from '../utils/utils-prescricao'
import { PesoPaper } from './components/PesoPaper'
import { DoseCellsSection } from './components/sections/DoseCellsSection'
import { DuracaoTratamentoSection } from './components/sections/DuracaoTratamentoSection'
import { FornecimentoFieldsCellsSection } from './components/sections/FornecimentoFieldsCellsSection'
import MedicamentoNaoCadastradoSection from './components/sections/MedicamentoNaoCadastradoSection'
import MedicamentoSection from './components/sections/MedicamentoSection'
import { MotivoPrescricaoSection } from './components/sections/MotivoPrescricaoSection'
import { PrescricaoMedicamentoFormActionButtons } from './components/sections/PrescricaoMedicamentoFormActionButtons'
import { PrescricaoMedicamentoHorusInfoCellSection } from './components/sections/PrescricaoMedicamentoHorusInfoCellSection'

const path = metaPath<MedicamentoFormModel>()

export interface AdicionarMedicamentoFormPanelProps extends FormRenderProps<MedicamentoFormModel> {
  isEdit: boolean
  isReplicar: boolean
  onClearEditedMedicamento(newEditedValue: MedicamentoFormModel): void
  medicamentoToEdit: MedicamentoFormModel
  onVerifyMedicamentoDuplicated(medicamento: MedicamentoFormModel): boolean
  showDoseAlert: boolean
  pesoAtual?: string
  pesoAoNascer?: string
  medicoesAnteriores?: MedicaoAnteriorFormModel[]
}

function AdicionarMedicamentoFormPanel(props: AdicionarMedicamentoFormPanelProps) {
  const {
    values,
    form,
    handleSubmit,
    isEdit,
    isReplicar,
    onClearEditedMedicamento,
    medicamentoToEdit,
    submitFailed,
    errors,
    onVerifyMedicamentoDuplicated,
    showDoseAlert,
    pesoAtual,
    pesoAoNascer,
    medicoesAnteriores,
  } = props

  const [activeAlert, setActiveAlert] = useState<AlertPrescricaoEnum | null>(null)
  const { MOTIVO_PRESCRICAO_ENABLED } = useFlags()

  const {
    atendimentoProfissional: { iniciadoEm: dataAtendimento },
    permissoes,
    prontuario: { id: prontuarioId },
    cidadao,
  } = useAtendimentoContext()

  const { meta: metaFrequenciaDuracaoError } = useField(path.frequenciaDuracao.absolutePath())
  const { meta: metaTotalDoseError } = useField(path.totalDoseError.absolutePath())
  const { meta: metaTurnosError } = useField(path.turnosError.absolutePath())
  const hasFrequenciaDuracaoError = !!metaFrequenciaDuracaoError.error
  const hasTotalDoseError = !!metaTotalDoseError.error && metaTotalDoseError.submitFailed
  const hasTurnosError = !!metaTurnosError.error && metaTurnosError.submitFailed
  const isRenovacao = !!medicamentoToEdit?.receitaUsoContinuoGrupoId
  const sexoCidadao = isEmpty(cidadao?.identidadeGeneroDbEnum) ? cidadao?.sexo : null
  const podeRegistrarCid = !permissoes.somenteCiap

  const { data, loading } = useMedicoesAtendimentosAnterioresQuery({
    variables: { prontuarioId, ultimoRegistroComPeso: true },
    skip: !isEmpty(pesoAtual),
  })
  const pesoCidadao = useMemoWithDeepCompare(() => {
    return calculatePeso(
      pesoAtual,
      dataAtendimento,
      data?.medicoes,
      mountMedicoesNoNascimento(pesoAoNascer, data?.medicoesNoNascimento, cidadao.dataNascimento),
      medicoesAnteriores
    )
  }, [pesoAtual, dataAtendimento, data, pesoAoNascer, cidadao.dataNascimento, medicoesAnteriores])

  const {
    horusData,
    statusConsulta,
    isHorusLoading,
    loadingConexaoInternet,
    executeConexaoQuery,
    setHorusData,
    handleMedicamentoChange,
  } = usePrescricaoHorusIntegration({ name: path })

  const {
    handleOnChangeTab,
    handleOnReset,
    handleOnCheckMedicamentoNaoCadastrado,
    handleOnChangeEscalaDuracao,
    handleOnChangeTipoDose,
    handleOnChangeUsoContinuo,
    handleOnSubmitClick,
  } = usePrescricaoFormManager({
    name: path,
    isEdit,
    dataAtendimento,
    podeRegistrarCid,
    onClearEditedMedicamento,
    onVerifyMedicamentoDuplicated,
    setHorusData,
    setActiveAlert,
    onSubmit: handleSubmit,
    isRenovacao,
    isReplicar,
    medicamentoToEdit,
  })

  const posologiaLabel = useMemo(() => {
    return createPosologiaLabel({
      tipoFrequencia: values.tipoFrequencia,
      intervaloDose: values.intervaloDose,
      frequenciaDose: values.frequenciaDose,
      quantidadePeriodoFrequencia: values.quantidadePeriodoFrequenciaTurno,
      unidadeMedidaTempoFrequencia: values.unidadeMedidaTempoFrequenciaTurno,
      turno: values.turno,
      qtDose: values.qtDose,
      unidadeMedida: values.unidadeMedidaDose,
      qtDoseManha: values.qtDoseManha,
      qtDoseTarde: values.qtDoseTarde,
      qtDoseNoite: values.qtDoseNoite,
      tipoDose: values.tipoDose,
    })
  }, [values])

  const dataFimTratamento = useMemo(() => {
    return calculateDataFimTratamento(values.duracao, values.dataInicioTratamento, values.escalaDuracao)
  }, [values.duracao, values.dataInicioTratamento, values.escalaDuracao])

  useEffect(() => {
    if (values.isVersaoAnterior) form.getRegisteredFields().forEach((field) => form.blur(field))
  }, [form, values.isVersaoAnterior])

  return (
    <VFlow
      style={css`
        margin-top: 1rem;
      `}
      vSpacing={2}
    >
      {activeAlert !== null && (
        <AlertPrescricaoModal
          tipoAlertaPrescricao={submitFailed && errors ? AlertPrescricaoEnum.CAMPO_INCORRETO : activeAlert}
          onCloseClick={() => setActiveAlert(null)}
        />
      )}
      {showDoseAlert && (
        <Alert type='warning' inline>
          O campo de dose foi atualizado. Adapte a informação abaixo nos novos campos:
          <br />
          <b>{values.dose}</b>
        </Alert>
      )}
      <FormPrompt condition={!isReplicar && isEdit} />
      <Grid gap={1}>
        <Cell size={12}>
          <Tooltip text={!values.registroManual && 'Utilize esta opção para preencher as informações livremente'}>
            <span>
              <CheckboxField
                name={path.registroManual}
                label='Preencher manualmente. Medicamento não encontrado na lista.'
                onChange={handleOnCheckMedicamentoNaoCadastrado}
                data-cy='RegistroManualCheckbox'
                disabled={isRenovacao}
              />
            </span>
          </Tooltip>
        </Cell>
        {values.registroManual ? (
          <MedicamentoNaoCadastradoSection path={path} disabled={isRenovacao} />
        ) : (
          <MedicamentoSection path={path} onChange={handleMedicamentoChange} disabled={isRenovacao} />
        )}
        <Cell size={7} data-cy='ViaAdministracao'>
          <ViaAdministracaoSelectField
            name={path.viaAdministracao}
            label='Via de administração'
            onKeyUp={handleKeyEnter}
            required
            data-cy='ViaAdministracaoCombobox'
          />
        </Cell>
        {values.registroManual ? (
          <Cell size={5} data-cy='TipoReceita'>
            <TipoReceitaSelectField
              name={path.tipoReceita}
              label='Tipo de receita'
              onKeyUp={handleKeyEnter}
              required
              data-cy='TipoReceitaCombobox'
              disabled={isRenovacao}
            />
          </Cell>
        ) : (
          <Cell size={5}>
            <VFlow vSpacing={0.2}>
              <Text fontWeight='bold'>Tipo de receita</Text>
              <Text data-cy='TipoReceitaLabel'>
                {tipoReceitaRecord[values?.principioAtivoCombo?.principioAtivo?.listaMaterial?.tipoReceita] ?? '-'}
              </Text>
            </VFlow>
          </Cell>
        )}
        {values.isVersaoAnterior && (
          <Cell size={12}>
            <Alert type='info' inline>
              <Text fontWeight='bolder' color='primary'>
                Posologia registrada em versão anterior:
              </Text>
              {` ${medicamentoToEdit?.posologia}`}
            </Alert>
          </Cell>
        )}

        <DoseCellsSection
          name={path}
          onTipoDoseChange={handleOnChangeTipoDose}
          hasTotalDoseError={hasTotalDoseError}
          formaFarmaceuticaDisabled={isRenovacao}
        />

        <Cell size={4} style={styles.pesoCell}>
          {loading ? <PageLoading message='Carregando peso do cidadão' /> : <PesoPaper medicaoPeso={pesoCidadao} />}
        </Cell>
        <FrequenciaTabs
          tipoDose={values.tipoDose}
          tipoFrequencia={values.tipoFrequencia}
          handleOnChangeTab={handleOnChangeTab}
          hasFrequenciaDuracaoError={hasFrequenciaDuracaoError}
          hasTotalDoseError={hasTotalDoseError}
          hasTurnosError={hasTurnosError}
        />
        <Cell size={12}>
          <VFlow vSpacing={0.6}>
            <Heading level={4}>Posologia</Heading>
            <HFlow>
              <Box style={styles.posologiaBox}>
                <Text fontWeight='bold' data-cy='PosologiaBox'>
                  {posologiaLabel?.trim() ? posologiaLabel : 'Nenhuma informação adicionada'}{' '}
                </Text>
              </Box>
            </HFlow>
          </VFlow>
        </Cell>
        <Cell size={12}>
          <DuracaoTratamentoSection
            name={path}
            dataAtendimento={dataAtendimento}
            onEscalaDuracaoChange={handleOnChangeEscalaDuracao}
            dataFimTratamento={dataFimTratamento}
          />
        </Cell>
        <Cell size={12}>
          <CheckboxField
            name={path.usoContinuo}
            label='Uso contínuo'
            data-cy='UsoContinuoCheckbox'
            onChange={handleOnChangeUsoContinuo}
            disabled={isRenovacao}
          />
        </Cell>
        <Cell size={12} data-cy='Recomendacoes'>
          <TextAreaField
            style={styles.recomendacoesTextArea}
            name={path.recomendacoes}
            label='Recomendações'
            maxLength={500}
            placeholder='Insira as recomendações para o uso do medicamento, detalhes da dose ou outras informações relevantes.'
            data-cy='RecomendacoesTextArea'
          />
        </Cell>
        {MOTIVO_PRESCRICAO_ENABLED && (
          <Cell size={9}>
            <MotivoPrescricaoSection
              name={path}
              podeRegistrarCid={podeRegistrarCid}
              sexoCidadao={sexoCidadao}
              idadeCidadaoEmAnos={cidadao?.idadeEmAnos}
            />
          </Cell>
        )}
        {(values.principioAtivoCombo || horusData) && (
          <Cell size={12}>
            <FormLabel label='Integração com Hórus' />
          </Cell>
        )}
        <FornecimentoFieldsCellsSection name={path} />
        <PrescricaoMedicamentoHorusInfoCellSection
          name={path}
          horusData={horusData}
          statusConsulta={statusConsulta}
          isHorusLoading={isHorusLoading}
          loadingConexaoInternet={loadingConexaoInternet}
          executeConexaoQuery={executeConexaoQuery}
        />
        <Cell size={12} style={styles.actionButtonCell}>
          <PrescricaoMedicamentoFormActionButtons
            isRenovacao={isRenovacao}
            onReset={() => handleOnReset(values.registroManual)}
            onSubmit={() => handleOnSubmitClick(posologiaLabel, dataFimTratamento)}
          />
        </Cell>
      </Grid>
    </VFlow>
  )
}

export default React.memo(AdicionarMedicamentoFormPanel)

const styles = {
  pesoCell: css`
    margin-top: 0.5rem;
  `,
  posologiaBox: css`
    min-height: 2.65rem;
    padding: 0.625rem 1rem;
    background: ${theme.pallete.status.info.background};
    border: 1px solid ${theme.pallete.status.info.main};
  `,
  recomendacoesTextArea: css`
    height: 5rem;
    min-height: 3rem;
    max-height: 10rem;
    resize: vertical;
  `,
  actionButtonCell: css`
    margin-top: 1.5rem;
  `,
}
